import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface Star {
  id: number;
  size: number;
  x: number; // in vw
  y: number; // resting y in vh
  opacity: number;
  twinkleDuration: number;
  landingDuration: number;
  speed: number; // initial cinematic speed
  initialY: number; // starting y (vh)
}

const NUM_STARS = 350;

const randomBetween = (min: number, max: number) => Math.random() * (max - min) + min;

const StarBackground: React.FC<{ darkMode: boolean }> = ({ darkMode }) => {
  const [stars, setStars] = useState<Star[]>([]);
  const [landingComplete, setLandingComplete] = useState(false);

  useEffect(() => {
    const generatedStars: Star[] = [];
    for (let i = 0; i < NUM_STARS; i++) {
      const finalY = randomBetween(10, 90); // final resting position
      generatedStars.push({
        id: i,
        size: randomBetween(0.5, 3),
        x: randomBetween(0, 100),
        y: finalY,
        initialY: -randomBetween(20, 100), // start above screen
        opacity: randomBetween(0.3, 0.9),
        twinkleDuration: randomBetween(1.5, 3),
        landingDuration: randomBetween(0.8, 1.5),
        speed: randomBetween(150, 400), // pixels per second for cinematic fall
      });
    }
    setStars(generatedStars);

    const maxLanding = Math.max(...generatedStars.map((s) => s.landingDuration)) * 1000;
    const timer = setTimeout(() => setLandingComplete(true), maxLanding);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden">
      <div
        className={`absolute inset-0 transition-colors duration-700 ${
          darkMode
            ? "bg-gradient-to-b from-black via-gray-900 to-gray-800"
            : "bg-gradient-to-b from-white via-gray-100 to-gray-200"
        }`}
      />

      <AnimatePresence>
        {stars.map((star) => (
          <motion.div
            key={star.id}
            className={`absolute rounded-full ${darkMode ? "bg-white" : "bg-black"}`}
            style={{
              width: star.size,
              height: star.size,
              left: `${star.x}vw`,
              opacity: star.opacity,
              boxShadow: `0 0 ${star.size * 2}px ${darkMode ? "white" : "black"}`, // faint trail/glow
            }}
            initial={{ y: `${star.initialY}vh` }}
            animate={{
              y: landingComplete
                ? [`${star.y}vh`, `${star.y + 5}vh`, `${star.y}vh`] // drifting downward
                : `${star.y}vh`, // cinematic entry
              opacity: [0, star.opacity, star.opacity],
            }}
            transition={{
              y: landingComplete
                ? { repeat: Infinity, repeatType: "mirror", duration: randomBetween(10, 20), ease: "linear" }
                : { duration: star.landingDuration, ease: [0.5, 0, 0.7, 1] }, // cubic-bezier for fast landing
              opacity: { duration: star.twinkleDuration, repeat: Infinity, repeatType: "loop", ease: "easeInOut" },
            }}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default StarBackground;
